/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import {
  Button,
  Col,
  Form,
  Row,
  Table,
  Card,
  InputGroup
} from 'react-bootstrap'
import api from '../../config/AxiosInstance'
import TablePagination from '../../components/atoms/Table/TablePagination'
import { Search, Download, RotateCcw, Copy } from 'lucide-react'
import Loader from '../../components/atoms/Loader'
import { useLocation, useNavigate } from 'react-router-dom'
import Select from 'react-select'

const cardFilterStyle = {
  backgroundColor: '#fff',
  borderRadius: '12px',
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  padding: '1.5rem',
  marginBottom: '1.5rem'
}

const labelStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.5rem',
  letterSpacing: '0.5px'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const FormControlStyle = {
  height: '45px',
  borderRadius: '10px',
  border: '1px solid #e9ecef',
  backgroundColor: '#ffffff',
  fontSize: '0.9rem'
}

const customStyles = {
  control: (base, state) => ({
    ...base,
    background: '#fff',
    borderColor: state.isFocused ? '#f89d1d' : '#e9ecef',
    borderRadius: '8px',
    boxShadow: state.isFocused
      ? '0 0 0 0.2rem rgba(248, 157, 29, 0.25)'
      : 'none',
    fontSize: '0.9rem',
    padding: '2px',
    '&:hover': {
      borderColor: '#f89d1d'
    }
  }),
  placeholder: (base) => ({
    ...base,
    color: '#adb5bd'
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? '#f89d1d'
      : state.isFocused
        ? '#fff3cd'
        : null,
    color: state.isSelected ? 'white' : '#333',
    fontSize: '0.9rem',
    cursor: 'pointer',
    ':active': {
      ...base[':active'],
      backgroundColor: '#f89d1d'
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: '0 8px'
  }),
  input: (provided) => ({
    ...provided,
    margin: '0px'
  })
}

function DisposalPage() {
  const navigate = useNavigate()
  const { state } = useLocation()
  const [warehouses, setWarehouses] = useState([])
  const [selectedWarehouse, setSelectedWarehouse] = useState(null)
  const [filter, setFilter] = useState({
    warehouse_id: state?.warehouse_id || ''
  })
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')

  const [loadingDownloadBeritaAcara, setLoadingDownloadBeritaAcara] = useState(
    {}
  )
  const [loadingDownloadGenerateSj, setLoadingDownloadGenerateSj] = useState({})

  const [disposals, setDisposals] = useState([])
  const [loading, setLoading] = useState(false)
  const [meta, setMeta] = useState({
    per_page: 10,
    current_page: 1,
    total_row: 0,
    total_page: 0
  })

  const headerTitleStyle = {
    color: '#343a40',
    fontSize: '1.5rem',
    fontWeight: '700',
    letterSpacing: '-0.5px'
  }

  const fetchWarehouse = async () => {
    try {
      const params = {
        is_parent: 1,
        forUser: 1
      }
      const response = await api.get('/master/warehouses/all', { params })
      const { data } = response.data
      const options = data.map((w) => ({
        value: w.id,
        label: w.warehouse_name
      }))
      setWarehouses(options)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    }
  }

  const fetchDisposal = async () => {
    try {
      setLoading(true)
      const params = {
        per_page: meta.per_page,
        page: meta.current_page,
        warehouse_id: filter.warehouse_id
      }
      if (debouncedSearch) params.q = debouncedSearch

      const response = await api.get('/disposal', {
        params
      })
      const { data, metadata } = response.data
      setDisposals(data)
      setMeta(metadata)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      toast.error(errMsg, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoading(false)
    }
  }

  const renderAction = (data) => (
    <div key={data.id}>
      <button
        className="btn btn-light text-secondary border rounded"
        style={{ width: '32px', height: '32px', padding: 0 }}
        onClick={() =>
          navigate(`/disposal/detail/${data.id}`, {
            state: {
              code: data.code,
              warehouse_id: filter.warehouse_id
            }
          })
        }
        title="View Detail"
      >
        <Search size={16} />
      </button>
    </div>
  )

  const handleDownloadBeritaAcara = async (eventId) => {
    try {
      setLoadingDownloadBeritaAcara((prev) => ({ ...prev, [eventId]: true }))
      const res = await api.get(`/berita-acara/${eventId}`, {
        responseType: 'arraybuffer'
      })
      const { status, data } = res
      if (status === 200) {
        const blob = new Blob([data], { type: 'application/pdf' })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `berita_acara_${eventId}.pdf`)
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const err = error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoadingDownloadBeritaAcara((prev) => ({ ...prev, [eventId]: false }))
    }
  }

  const handleDownloadSj = async (disposalId) => {
    try {
      setLoadingDownloadGenerateSj((prev) => ({ ...prev, [disposalId]: true }))
      const res = await api.get(`disposal/${disposalId}/SJ/`, {
        responseType: 'arraybuffer'
      })
      const { status, data } = res
      if (status === 200) {
        const blob = new Blob([data], { type: 'application/pdf' })

        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `generate_sj_${disposalId}.pdf`)
        document.body.appendChild(link)
        link.click()

        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      }
    } catch (error) {
      const err = error?.response?.data?.errors?.message || 'An error occurred'
      toast.error(err, {
        position: 'top-right',
        style: {
          fontSize: '14px',
          fontWeight: '600'
        }
      })
    } finally {
      setLoadingDownloadGenerateSj((prev) => ({ ...prev, [disposalId]: false }))
    }
  }

  const renderDownloadBeritaAcara = (data) => (
    <div key={data.id}>
      <Button
        size="sm"
        className={`btn ${
          loadingDownloadBeritaAcara[data.event_id]
            ? 'btn-secondary'
            : 'btn-primary-blue'
        } text-uppercase rounded px-3`}
        onClick={() => handleDownloadBeritaAcara(data.event_id)}
        disabled={loadingDownloadBeritaAcara[data.event_id]}
      >
        {loadingDownloadBeritaAcara[data.event_id]
          ? 'Downloading...'
          : 'Download'}
      </Button>
    </div>
  )

  const renderDownloadSjDisposal = (data) => (
    <div key={data.id} className="text-end">
      <Button
        className="btn btn-light text-secondary border rounded"
        style={{ width: '32px', height: '32px', padding: 0 }}
        onClick={() => handleDownloadSj(data.id)}
        disabled={loadingDownloadGenerateSj[data.id]}
        title="Download SJ"
      >
        <Download size={16} />
      </Button>
    </div>
  )

  const columns = [
    {
      key: 'code',
      label: 'Ticket',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          {data.code ?? '-'}

          {data.code && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(data.code)
                toast.success('Copied to clipboard')
              }}
              title="Copy"
            />
          )}
        </div>
      )
    },
    { key: 'warehouse_id', label: 'Warehouse' },
    { key: 'created_at', label: 'Created Date' },
    {
      key: 'erp_code',
      label: '#ERP Doc No',
      component: (data) => (
        <div className="d-flex align-items-center gap-2">
          {data.erp_code ?? '-'}

          {data.erp_code && (
            <Copy
              size={14}
              className="text-muted cursor-pointer"
              style={{
                cursor: 'pointer',
                opacity: 0.5,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = 1)}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = 0.5)}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                navigator.clipboard.writeText(data.erp_code)
                toast.success('Copied to clipboard')
              }}
              title="Copy"
            />
          )}
        </div>
      )
    },
    { key: 'sj_disposal', label: 'SJ Disposal' },
    { key: 'download_berita_acara', label: 'Berita Acara' },
    { key: 'action', label: 'Action', component: (data) => renderAction(data) }
  ]

  const handlePageChange = (data) => {
    setMeta((prevMeta) => ({ ...prevMeta, current_page: data.selected + 1 }))
    window.scrollTo(0, 0)
  }

  const handleOnChangeMeta = (e) => {
    setMeta((prevMeta) => ({
      ...prevMeta,
      per_page: parseInt(e.target.value, 10),
      current_page: 1
    }))
  }

  const resetFilter = () => {
    setFilter({ warehouse_id: '' })
    setSelectedWarehouse(null)
    setSearch('')
    setDebouncedSearch('')
    setMeta((prev) => ({ ...prev, current_page: 1 }))
    setDisposals([])
  }

  useEffect(() => {
    fetchWarehouse()
  }, [])

  useEffect(() => {
    if (warehouses.length > 0 && filter.warehouse_id) {
      const found = warehouses.find((w) => w.value === filter.warehouse_id)
      if (found) setSelectedWarehouse(found)
    }
  }, [warehouses])

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search)
      setMeta((prev) => ({ ...prev, current_page: 1 }))
    }, 1000)
    return () => clearTimeout(handler)
  }, [search])

  useEffect(() => {
    if (
      (filter.warehouse_id && filter.warehouse_id !== '') ||
      debouncedSearch
    ) {
      fetchDisposal()
    } else if (filter.warehouse_id === '' && !debouncedSearch) {
      if (!debouncedSearch) setDisposals([])
    }
  }, [meta.current_page, meta.per_page, filter.warehouse_id, debouncedSearch])

  return (
    <div className="container-fluid px-4 mb-5">
      <Toaster />
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 style={headerTitleStyle}>Disposal</h3>
        </Col>
        <Col className="text-end">
          <Button
            variant="light"
            className="text-secondary fw-bold rounded px-3 border"
            onClick={resetFilter}
            style={{ fontSize: '0.85rem' }}
          >
            <RotateCcw size={16} className="me-2" />
            Reset Filters
          </Button>
        </Col>
      </Row>

      <Card style={cardFilterStyle}>
        <Row className="gy-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Search</Form.Label>
              <InputGroup>
                <InputGroup.Text
                  style={{
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRight: 'none',
                    borderTopLeftRadius: '10px',
                    borderBottomLeftRadius: '10px'
                  }}
                >
                  <Search size={18} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by tiket or erp doc"
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value)
                  }}
                  style={{
                    ...FormControlStyle,
                    borderLeft: 'none',
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                />
              </InputGroup>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label style={labelStyle}>Warehouse Name</Form.Label>
              <Select
                options={warehouses}
                value={selectedWarehouse}
                onChange={(opt) => {
                  setSelectedWarehouse(opt)
                  setFilter({ ...filter, warehouse_id: opt ? opt.value : '' })
                  setMeta((prev) => ({ ...prev, current_page: 1 }))
                }}
                placeholder="Select Warehouse"
                styles={customStyles}
                isClearable
              />
            </Form.Group>
          </Col>
        </Row>
      </Card>

      <Row>
        <Col md={12}>
          <Table
            hover
            responsive
            className="shadow-sm mb-5 bg-white"
            style={{
              fontSize: '0.9rem',
              borderRadius: '12px',
              overflow: 'hidden'
            }}
          >
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                {columns.map((column) => (
                  <th
                    className="p-2 align-middle"
                    key={column.key}
                    style={tableHeaderStyle}
                  >
                    <span>{column.label}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={columns.length} className="p-5 text-center">
                    <Loader />
                  </td>
                </tr>
              )}
              {!loading && disposals?.length === 0 && (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="p-5 text-center text-muted fw-bold"
                  >
                    No Data Available
                  </td>
                </tr>
              )}
              {!loading &&
                disposals?.length > 0 &&
                disposals?.map((disposal, indexStock) => (
                  <tr
                    key={indexStock}
                    style={{ borderBottom: '1px solid #f0f0f0' }}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className="p-2 align-middle">
                        {column.key === 'download_berita_acara' &&
                          renderDownloadBeritaAcara(disposal)}
                        {column.key === 'sj_disposal' &&
                          renderDownloadSjDisposal(disposal)}
                        {column.component && column.component(disposal)}
                        {!column.component && disposal[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
            </tbody>
            <tfoot>
              <tr>
                <td className="p-3 border-top-0" colSpan={columns.length}>
                  <TablePagination
                    meta={meta}
                    handlePageChange={handlePageChange}
                    handleOnChangeMeta={handleOnChangeMeta}
                  />
                </td>
              </tr>
            </tfoot>
          </Table>
        </Col>
      </Row>
    </div>
  )
}

export default DisposalPage
